/**
 * Conflict Resolution Dialog
 * Shows data conflicts between local and server for manual resolution
 */

import React, { useState } from 'react';
import { AlertTriangle, Check, X, RefreshCw, GitMerge, Server, Smartphone } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { resolveConflictManually, resolveConflictWithMerge } from '../utils/syncEngine';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const ConflictResolutionDialog = ({ 
  open, 
  onOpenChange, 
  conflicts, 
  onResolved 
}) => {
  const { token } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isResolving, setIsResolving] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState(null);

  const currentConflict = conflicts[currentIndex];
  const hasMoreConflicts = currentIndex < conflicts.length - 1;

  const formatEntityType = (type) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatFieldName = (name) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return 'Not set';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Find differences between local and server data
  const getDifferences = () => {
    if (!currentConflict) return [];
    
    const local = currentConflict.local_data || {};
    const server = currentConflict.server_data || {};
    const allKeys = new Set([...Object.keys(local), ...Object.keys(server)]);
    
    const differences = [];
    for (const key of allKeys) {
      // Skip internal fields
      if (key.startsWith('_') || key === 'id') continue;
      
      const localValue = local[key];
      const serverValue = server[key];
      
      if (JSON.stringify(localValue) !== JSON.stringify(serverValue)) {
        differences.push({
          field: key,
          local: localValue,
          server: serverValue
        });
      }
    }
    
    return differences;
  };

  const handleResolve = async (resolution) => {
    if (!currentConflict || isResolving) return;
    
    setIsResolving(true);
    try {
      await resolveConflictManually(currentConflict.id, resolution, token);
      toast.success(`Conflict resolved using ${resolution} data`);
      
      if (hasMoreConflicts) {
        setCurrentIndex(prev => prev + 1);
        setSelectedResolution(null);
      } else {
        onResolved?.();
        onOpenChange(false);
      }
    } catch (error) {
      toast.error('Failed to resolve conflict: ' + error.message);
    } finally {
      setIsResolving(false);
    }
  };

  const handleSkip = () => {
    if (hasMoreConflicts) {
      setCurrentIndex(prev => prev + 1);
      setSelectedResolution(null);
    } else {
      onOpenChange(false);
    }
  };

  if (!currentConflict) return null;

  const differences = getDifferences();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Data Conflict Detected
          </DialogTitle>
          <DialogDescription>
            Changes were made both locally (offline) and on the server. Choose which version to keep.
          </DialogDescription>
        </DialogHeader>

        {/* Conflict info */}
        <div className="flex items-center justify-between py-2 border-b">
          <div className="flex items-center gap-2">
            <Badge variant="outline">{formatEntityType(currentConflict.entity_type)}</Badge>
            <span className="text-sm text-slate-500">ID: {currentConflict.entity_id}</span>
          </div>
          <Badge variant="secondary">
            {currentIndex + 1} of {conflicts.length} conflicts
          </Badge>
        </div>

        {/* Differences */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="space-y-4 p-4">
            {differences.length === 0 ? (
              <p className="text-center text-slate-500">No visible differences detected</p>
            ) : (
              differences.map(diff => (
                <Card key={diff.field} className="overflow-hidden">
                  <CardHeader className="py-2 px-4 bg-slate-50">
                    <CardTitle className="text-sm font-medium">
                      {formatFieldName(diff.field)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="grid grid-cols-2 divide-x">
                      {/* Local value */}
                      <div 
                        className={`p-4 cursor-pointer transition-colors ${
                          selectedResolution === 'local' 
                            ? 'bg-blue-50 border-l-4 border-blue-500' 
                            : 'hover:bg-slate-50'
                        }`}
                        onClick={() => setSelectedResolution('local')}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Smartphone className="w-4 h-4 text-blue-500" />
                          <span className="text-xs font-medium text-blue-600">LOCAL (Your Device)</span>
                        </div>
                        <pre className="text-sm text-slate-700 whitespace-pre-wrap break-words font-mono bg-slate-100 p-2 rounded">
                          {formatValue(diff.local)}
                        </pre>
                      </div>
                      
                      {/* Server value */}
                      <div 
                        className={`p-4 cursor-pointer transition-colors ${
                          selectedResolution === 'server' 
                            ? 'bg-green-50 border-l-4 border-green-500' 
                            : 'hover:bg-slate-50'
                        }`}
                        onClick={() => setSelectedResolution('server')}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Server className="w-4 h-4 text-green-500" />
                          <span className="text-xs font-medium text-green-600">SERVER</span>
                        </div>
                        <pre className="text-sm text-slate-700 whitespace-pre-wrap break-words font-mono bg-slate-100 p-2 rounded">
                          {formatValue(diff.server)}
                        </pre>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Actions */}
        <DialogFooter className="border-t pt-4">
          <div className="flex items-center justify-between w-full gap-4">
            <Button variant="ghost" onClick={handleSkip} disabled={isResolving}>
              {hasMoreConflicts ? 'Skip' : 'Close'}
            </Button>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => handleResolve('local')}
                disabled={isResolving}
                className={selectedResolution === 'local' ? 'border-blue-500 bg-blue-50' : ''}
              >
                {isResolving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Smartphone className="w-4 h-4 mr-2" />
                )}
                Keep Local
              </Button>
              
              <Button
                onClick={() => handleResolve('server')}
                disabled={isResolving}
                className={selectedResolution === 'server' ? 'bg-green-600 hover:bg-green-700' : 'btn-primary'}
              >
                {isResolving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Server className="w-4 h-4 mr-2" />
                )}
                Keep Server
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConflictResolutionDialog;
